import { useState, useCallback, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * HeritageImagePopup - Reusable heritage image component with informational pop-up
 * 
 * Desktop: Hover shows pop-up with image thumbnail and descriptive text
 * Mobile: Tap opens pop-up modal
 * Both: Clean archival aesthetic with subtle shadow
 */
export const HeritageImagePopup = ({ 
  imageSrc,
  imageAlt = 'Heritage Image',
  title,
  subtitle,
  bodyText,
  className = '',
}) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const popupRef = useRef(null);
  const containerRef = useRef(null);

  // Detect touch device
  useEffect(() => {
    const checkTouch = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    checkTouch();
  }, []);

  // Handle mouse enter (desktop hover)
  const handleMouseEnter = useCallback(() => {
    if (isTouchDevice) return;
    
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    setIsPopupOpen(true);
  }, [isTouchDevice]);

  // Handle mouse leave (desktop)
  const handleMouseLeave = useCallback(() => {
    if (isTouchDevice) return;
    
    hoverTimeoutRef.current = setTimeout(() => {
      setIsPopupOpen(false);
    }, 200);
  }, [isTouchDevice]);

  // Handle popup mouse enter (keep open when hovering popup)
  const handlePopupMouseEnter = useCallback(() => {
    if (isTouchDevice) return;
    
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, [isTouchDevice]);

  // Handle popup mouse leave
  const handlePopupMouseLeave = useCallback(() => {
    if (isTouchDevice) return;
    
    hoverTimeoutRef.current = setTimeout(() => {
      setIsPopupOpen(false);
    }, 200);
  }, [isTouchDevice]);

  // Handle click/tap (mobile)
  const handleClick = useCallback((e) => {
    if (isTouchDevice) {
      e.preventDefault();
      e.stopPropagation();
      setIsPopupOpen(true);
    }
  }, [isTouchDevice]);

  // Close popup
  const closePopup = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsPopupOpen(false);
  }, []);

  // Handle backdrop click (mobile - clicking outside closes modal)
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      closePopup();
    }
  }, [closePopup]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      closePopup();
    }
  }, [closePopup]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Focus trap for accessibility when popup is open
  useEffect(() => {
    if (isPopupOpen && popupRef.current) {
      popupRef.current.focus();
    }
  }, [isPopupOpen]);

  return (
    <div 
      ref={containerRef}
      className={`heritage-image-container relative ${className}`}
    >
      {/* Main Image */}
      <button
        type="button"
        className="heritage-image-trigger w-full h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label={`View details about ${title}`}
        aria-haspopup="dialog"
        aria-expanded={isPopupOpen}
        data-testid="heritage-image-trigger"
      >
        <img 
          src={imageSrc}
          alt={imageAlt}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-[1.02]"
          draggable="false"
        />
        {/* Subtle overlay hint on hover */}
        <div className="absolute inset-0 bg-primary/0 hover:bg-primary/10 transition-colors duration-300 flex items-center justify-center">
          <span className="opacity-0 hover:opacity-100 transition-opacity duration-300 text-white text-sm font-medium bg-primary/70 px-3 py-1.5 rounded">
            {isTouchDevice ? 'Tap for details' : 'Hover for details'}
          </span>
        </div>
      </button>

      {/* Popup with backdrop - same behavior as logo popup */}
      {isPopupOpen && (
        <div 
          className="heritage-popup-backdrop"
          onClick={handleBackdropClick}
          onMouseEnter={!isTouchDevice ? handlePopupMouseEnter : undefined}
          onMouseLeave={!isTouchDevice ? handlePopupMouseLeave : undefined}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          ref={popupRef}
        >
          {/* Popup card */}
          <div 
            className="heritage-popup-card"
            onMouseEnter={handlePopupMouseEnter}
            onMouseLeave={handlePopupMouseLeave}
          >
            {/* Close button */}
            <button
              onClick={closePopup}
              className={`heritage-popup-close ${isTouchDevice ? 'touch-device' : 'desktop-device'}`}
              aria-label="Close details"
              data-testid="heritage-popup-close"
            >
              <X className="h-5 w-5" />
            </button>
            
            {/* Thumbnail image */}
            <div className="heritage-popup-thumbnail">
              <img 
                src={imageSrc}
                alt={imageAlt}
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Text content */}
            <div className="heritage-popup-content">
              {/* Title */}
              <h3 className="heritage-popup-title">
                {title}
              </h3>
              
              {/* Subtitle (e.g., date) */}
              {subtitle && (
                <p className="heritage-popup-subtitle">
                  {subtitle}
                </p>
              )}
              
              {/* Body text - can contain multiple paragraphs */}
              <div className="heritage-popup-body">
                {Array.isArray(bodyText) ? (
                  bodyText.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))
                ) : (
                  <p>{bodyText}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeritageImagePopup;
